/**
 * SF-4758: one row of my ranking. Drag it by the handle (mouse, touch, or Space then the arrow
 * keys, via dnd-kit), or use the up and down buttons, so ranking never needs a mouse.
 */
import { Button, Space, Typography } from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CloseOutlined,
  HolderOutlined,
} from '@ant-design/icons';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { PriorityItem } from '../api/prioritiesClient';

interface Props {
  item: PriorityItem;
  /** SF-5087: false for rows past N, which do not count in the results and show greyed. */
  counts?: boolean;
  position: number;
  isFirst: boolean;
  isLast: boolean;
  onUp: () => void;
  onDown: () => void;
  onRemove: () => void;
}

export const RankedRow: React.FC<Props> = ({
  item,
  counts = true,
  position,
  isFirst,
  isLast,
  onUp,
  onDown,
  onRemove,
}) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition } =
    useSortable({ id: item.id });

  return (
    <li
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 0',
        borderBottom: '1px solid #f0f0f0',
        background: '#fff',
        opacity: counts ? 1 : 0.5,
      }}
    >
      <Button
        ref={setActivatorNodeRef}
        type="text"
        size="small"
        icon={<HolderOutlined />}
        aria-label={`Drag ${item.title}`}
        style={{ cursor: 'grab' }}
        {...attributes}
        {...listeners}
      />
      <Typography.Text strong style={{ width: 24 }}>
        {position}.
      </Typography.Text>
      <Typography.Text style={{ flex: 1 }}>{item.title}</Typography.Text>
      <Space size={2}>
        <Button
          size="small"
          icon={<ArrowUpOutlined />}
          aria-label={`Move ${item.title} up`}
          disabled={isFirst}
          onClick={onUp}
        />
        <Button
          size="small"
          icon={<ArrowDownOutlined />}
          aria-label={`Move ${item.title} down`}
          disabled={isLast}
          onClick={onDown}
        />
        <Button
          size="small"
          icon={<CloseOutlined />}
          aria-label={`Remove ${item.title}`}
          onClick={onRemove}
        />
      </Space>
    </li>
  );
};
